// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    getWishListsParameter: GetWishListsApiParameter;
    updateLoadParameter: Function;
}

const mapStateToProps = (state: ApplicationState) => {
    const { searchResults, selectedProductInfo } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;

    return {
        searchResults,
        product,
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    reset,
};

export interface MyListsSearchFieldProductNumberStyles {
    wrapper?: SearchFieldWrapperStyles;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
}

export const searchFieldProductNumberStyles: MyListsSearchFieldProductNumberStyles = {
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
};
const styles = searchFieldProductNumberStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyListsSearchFieldProductNumber = ({
    getWishListsParameter,
    updateLoadParameter,
    searchResults,
    product,
    searchProducts,
    setProduct,
    reset,
}: Props) => {
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (getWishListsParameter.erpNumber === undefined) {
            setSearch("");
            setOptions([]);
            reset();
        }
    }, [getWishListsParameter]);

    useEffect(() => {
        setIsLoading(false);
        if (!searchResults) {
            return;
        }

        if (searchResults.length === 0) {
            const trimmedQuery = search.trim();
            setOptions([
                {
                    optionText: trimmedQuery,
                    optionValue: trimmedQuery,
                    rowChildren: <StyledWrapper {...styles.optionWrapper}>{trimmedQuery}</StyledWrapper>,
                },
            ]);
            return;
        }

        const newOptions = searchResults.map((product: any) => ({
            optionText: product.erpNumber,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper
                    {...styles.optionWrapper}
                    data-test-selector={`myLists_searchField_productNumber_${product.id}`}
                >
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage {...styles.productImage} src={product.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                        <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        if (product) {
            const erpNumber = product.productNumber;
            updateLoadParameter({ erpNumber });
        }
    }, [product]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            }
        } else {
            updateLoadParameter({ erpNumber: value });
        }
    };

    const debouncedSearchProducts = useCallback(
        debounce((query: string) => {
            setSearch(query);
            searchProducts({ query, relevancy: false });
        }, 300),
        [],
    );

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        debouncedSearchProducts(event.target.value);
    };

    return (
        <SearchFieldWrapper extendedStyles={styles.wrapper}>
            <DynamicDropdown
                {...styles.searchDynamicDropdown}
                label={translate("Product")}
                onSelectionChange={onSelectionChangeHandler}
                onInputChange={onInputChanged}
                filterOption={() => true}
                selected={product?.id || getWishListsParameter.erpNumber}
                options={options}
                // xng-change: make no options value appear to prevent the tiny shadow appear below the input field
                // hideNoOptionsIfEmptySearch
                isLoading={isLoading}
                placeholder={translate("Search for a product")}
                search={search}
                data-test-selector="myLists_searchField_productNumber"
            />
        </SearchFieldWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsSearchFieldProductNumber);
